/**
 * Ported VERBATIM from apr_template `src/pages/ResourceSkillsets.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './ApplicationDependencies.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_COLOR, WS_RADIUS, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const ResourceSkillsetsHelp: Help = {
  title: 'Resource Skillsets',
  icon: '🎯',
  purpose:
    'Maintain the master list of skillsets used across resource planning &mdash; each with a name and a description of the technologies and competencies it covers.',
  doHere: [
    'Click <strong>&#10133; Add Skillset</strong> to create a skillset with its name and description.',
    'Use <strong>&#128203; Download Template</strong> then <strong>&#128228; Upload File</strong> to bulk-import skillsets from CSV.',
    'Filter the table by name; <strong>&#128229; Download Data</strong> exports the current list.',
    'Use <strong>&#128065;</strong> / <strong>&#9999;&#65039;</strong> / <strong>&#128465;&#65039;</strong> to view, edit or delete a skillset.',
  ],
  fits: 'Integration &rarr; Resource Planning &rarr; Resource Skillsets',
  firstTime:
    'Add your core skillsets (e.g. Cloud Computing, DevOps), or download the template and bulk-upload them.',
  sections: [
    {
      type: 'html',
      label: 'Tip',
      html: 'Skillset names defined here become the selectable <strong>Resource Skillset</strong> values in Resource Capacity, so add them before building resource pools.',
    },
  ],
  links: [{ href: '/integration-framework/resource-capacity', label: 'Resource Capacity' }],
};

interface SkillsetMaster {
  skillset_id: string;
  skillset_name: string;
  skillset_description: string;
  created_at_formatted?: string;
  updated_at_formatted?: string;
}

const ResourceSkillsets: React.FC = () => {
  const [skillsets, setSkillsets] = useState<SkillsetMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterSkillsetName, setFilterSkillsetName] = useState<string>('');
  const [editingSkillset, setEditingSkillset] = useState<SkillsetMaster | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    skillsetId: '',
    skillsetName: '',
    skillsetDescription: ''
  });
  const { confirm, notify, dialogs } = useWsDialogs();

  useEffect(() => {
    fetchSkillsets();
  }, []);

  const fetchSkillsets = async () => {
    try {
      const response = await authFetch('/api/skillset-master');
      if (!response.ok) throw new Error('Failed to fetch');
      const result = await response.json();
      result.data.sort((a: SkillsetMaster, b: SkillsetMaster) => a.skillset_name.localeCompare(b.skillset_name));
      setSkillsets(result.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load skillset master records');
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0]!.split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i]!.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      // Transform CSV data to match API expected format (skillset_id is auto-generated)
      const transformedData = previewData.map(row => ({
        skillset_name: row['Skillset Name'] || row['skillset_name'] || '',
        skillset_description: row['Description'] || row['skillset_description'] || ''
      }));

      const response = await authFetch('/api/skillset-master/bulk-upload', {
        method: 'POST',
        body: JSON.stringify({ records: transformedData })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Successfully imported ${data.data.successfulRecords} skillset records`);
        fetchSkillsets();
        
        if (data.data.errors > 0) {
          const errorMessage = data.data.errorDetails.join('\n');
          setError(`Import completed with ${data.data.errors} errors:\n${errorMessage}`);
        }
      } else {
        setError(data.message || 'Failed to import skillsets');
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Skillset Name,Description
Cloud Computing,"AWS; Azure; GCP; Kubernetes; Docker; Terraform"
DevOps Engineering,"CI/CD; Jenkins; GitLab; Docker; Kubernetes; Infrastructure as Code"
Data Engineering,"SQL; Python; ETL; Data Warehousing; Big Data; Apache Spark"
Mobile Development,"iOS; Android; React Native; Flutter; Mobile UI/UX"
Web Development,"React; Angular; Vue; Node.js; HTML/CSS; JavaScript"
Quality Assurance,"Test Automation; Selenium; API Testing; Performance Testing"`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skillset_master_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (skillsets.length === 0) {
      notify('No data to download', 'info');
      return;
    }

    // Match template format: Skillset Name,Description
    const headers = ['Skillset Name', 'Description'];
    const csvRows = [headers.join(',')];

    skillsets.forEach(record => {
      const row = [
        `"${(record.skillset_name || '').replace(/"/g, '""')}"`,
        `"${(record.skillset_description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_skillsets_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleAddNew = () => {
    setEditingSkillset(null);
    setFormData({
      skillsetId: '',
      skillsetName: '',
      skillsetDescription: ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSkillset(null);
    setViewMode(false);
    setFormData({
      skillsetId: '',
      skillsetName: '',
      skillsetDescription: ''
    });
    setError('');
  };

  const handleView = (skillset: SkillsetMaster) => {
    setEditingSkillset(skillset);
    setFormData({
      skillsetId: skillset.skillset_id,
      skillsetName: skillset.skillset_name,
      skillsetDescription: skillset.skillset_description
    });
    setViewMode(true);
    setShowForm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // For new records, only send name and description (ID is auto-generated)
      // For updates, send name and description only (ID is in the URL)
      const payload = {
        skillset_name: formData.skillsetName,
        skillset_description: formData.skillsetDescription
      };

      const url = editingSkillset
        ? `/api/skillset-master/${editingSkillset.skillset_id}`
        : '/api/skillset-master';

      const method = editingSkillset ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(result.message);
        // Clear form but keep modal open for adding more entries
        setFormData({
          skillsetId: '',
          skillsetName: '',
          skillsetDescription: ''
        });
        fetchSkillsets();
        // Clear any error messages
        setError('');
      } else {
        setError(result.message || 'Failed to save skillset');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save skillset');
    }
  };

  const handleEdit = (skillset: SkillsetMaster) => {
    setEditingSkillset(skillset);
    setFormData({
      skillsetId: skillset.skillset_id,
      skillsetName: skillset.skillset_name,
      skillsetDescription: skillset.skillset_description
    });
    setViewMode(false);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (await confirm('Are you sure you want to delete this skillset?', { tone: 'destructive' })) {
      try {
        const response = await authFetch(`/api/skillset-master/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Skillset deleted successfully');
        fetchSkillsets();
      } catch (err: any) {
        setError(err.message || 'Failed to delete skillset');
      }
    }
  };

  if (loading) return <div className="md-loading">Loading skillset master records...</div>;

  const filteredSkillsets = skillsets.filter(skillset => {
    const nameMatch = filterSkillsetName === '' || 
      skillset.skillset_name.toLowerCase().includes(filterSkillsetName.toLowerCase());
    return nameMatch;
  });

  return (
    <div className="page">
      <HelpPanel help={ResourceSkillsetsHelp} />
      {/* Header */}
      <PageHeader title={<>Resource Skillsets Management</>} subtitle={<>Manage master skillsets and their descriptions for resource planning</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
<button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new skillset record"
            >
              <Icon icon={icons.Plus} size={20} /> Add Skillset
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              <Icon icon={icons.ClipboardList} size={20} /> Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import skillsets from CSV"
            >
              <Icon icon={icons.Upload} size={20} /> Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {skillsets.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
              >
                <Icon icon={icons.Download} size={20} /> Download Data
              </button>
            )}
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Filter Section */}
      {skillsets.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '100%'
          }}>
            <label style={{ minWidth: '120px' }}>Filter by Name:</label>
            <input
              type="text"
              value={filterSkillsetName}
              onChange={(e) => setFilterSkillsetName(e.target.value)}
              placeholder="Enter skillset name to filter..."
              style={{
                minWidth: '200px',
                flex: '1',
                maxWidth: '300px'
              }}
            />
            {filterSkillsetName && (
              <button
                className="btn"
                onClick={() => setFilterSkillsetName('')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Clear Filter
              </button>
            )}
            <span style={{
              ...wsType('meta'),
              color: WS_COLOR.textMuted,
              whiteSpace: 'nowrap'
            }}>
              Showing {filteredSkillsets.length} of {skillsets.length} skillsets
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card">
        {skillsets.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon"><Icon icon={icons.Target} size={24} /></div>
            <h3>No Skillsets Found</h3>
            <p>Use the action cards above to add skillsets or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container">
            <table className="md-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>Skillset Name</th>
                  <th style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>Description</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSkillsets.map(skillset => (
                  <tr key={skillset.skillset_id}>
                    <td style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600 }}>{skillset.skillset_name}</span>
                    </td>
                    <td style={{ textAlign: 'left', padding: '8px', maxWidth: '300px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {skillset.skillset_description}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                          icon={icons.Eye}
                          label="View skillset details"
                          title="View skillset details"
                          onClick={() => handleView(skillset)}
                        />
                        <IconButton
                          icon={icons.Pencil}
                          label="Edit skillset"
                          title="Edit skillset"
                          onClick={() => handleEdit(skillset)}
                        />
                        <IconButton
                          icon={icons.Trash2}
                          label="Delete skillset"
                          title="Delete skillset"
                          onClick={() => handleDelete(skillset.skillset_id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {/* Skillset Form — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showForm}
        title={viewMode ? 'View Skillset' : (editingSkillset ? 'Edit Skillset' : 'Add New Skillset')}
        onClose={handleCloseForm}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={handleCloseForm}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" form="rs-skillset-form" className="btn btn-primary">
                {editingSkillset ? 'Update Skillset' : 'Create Skillset'}
              </button>
            )}
          </>
        }
      >
              <form id="rs-skillset-form" onSubmit={handleSubmit}>
                {error && (
                  <div style={{ marginBottom: '16px' }}>
                    <Banner tone="error">{error}</Banner>
                  </div>
                )}

                <div className="md-form-section">
                  <h3>Basic Information</h3>
                  <div className="md-form-row">
                    {(editingSkillset || viewMode) && (
                      <div className="md-form-group">
                        <label>Skillset ID</label>
                        <input
                          type="text"
                          className="md-input"
                          name="skillsetId"
                          value={formData.skillsetId}
                          disabled
                          style={{
                            backgroundColor: WS_COLOR.surfaceSubtle,
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                    )}
                    <div className="md-form-group">
                      <label>Skillset Name *</label>
                      <input
                        type="text"
                        className="md-input"
                        name="skillsetName"
                        value={formData.skillsetName}
                        onChange={handleInputChange}
                        placeholder="e.g., Cloud Computing, Web Development"
                        required
                        maxLength={100}
                        disabled={viewMode}
                        style={{
                          backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                          cursor: viewMode ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="md-form-section">
                  <h3>Skillset Details</h3>
                  <div className="md-form-group">
                    <label>Description *</label>
                    <textarea
                      className="md-input"
                      name="skillsetDescription"
                      value={formData.skillsetDescription}
                      onChange={handleInputChange}
                      placeholder="Detailed description of skills and technologies included in this skillset (e.g., AWS; Azure; GCP; Kubernetes; Docker; Terraform)"
                      required
                      maxLength={300}
                      rows={4}
                      disabled={viewMode}
                      style={{
                        backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                        cursor: viewMode ? 'not-allowed' : 'text'
                      }}
                    />
                    <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: '4px' }}>
                      {formData.skillsetDescription.length}/300 characters - List specific technologies, tools, and competencies
                    </div>
                  </div>
                </div>

                {/* Cancel/Close and Create/Update are in the panel's fixed action row (Sheet `actions`). */}
              </form>
      </Sheet>

      {/* Upload — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showUploadModal}
        title="Preview Import Data"
        onClose={() => setShowUploadModal(false)}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={() => setShowUploadModal(false)}>
              Cancel
            </button>
            {previewData.length > 0 && (
              <button type="button" className="btn btn-primary" onClick={handleBulkImport}>
                Import {previewData.length} Records
              </button>
            )}
          </>
        }
      >
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field }}>
                    <table style={{ width: '100%', ...wsType('table') }}>
                      <thead style={{ backgroundColor: WS_COLOR.surfaceSubtle }}>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ ...wsType('tableHeader'), padding: '8px', textAlign: 'left', borderBottom: `1px solid ${WS_COLOR.border}` }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex} style={{ padding: '8px', borderBottom: `1px solid ${WS_COLOR.border}` }}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
      </Sheet>

      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')} aria-label="Close"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')} aria-label="Close"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {dialogs}
    </div>
  );
};

export default ResourceSkillsets;